export const CRAWLER_PERIODIC_CYCLE_DAYS = 14;
export const CRAWLER_PERIODIC_HOURS_PER_DAY = 24;
export const CRAWLER_PERIODIC_SLOTS =
  CRAWLER_PERIODIC_CYCLE_DAYS * CRAWLER_PERIODIC_HOURS_PER_DAY;
export const CRAWLER_PERIODIC_WINDOW_HOURS = 6;
export const CRAWLER_PERIODIC_INTERVAL_MS =
  CRAWLER_PERIODIC_CYCLE_DAYS * 24 * 60 * 60 * 1000;
export const CRAWLER_PERIODIC_DUE_GRACE_MS = 60 * 60 * 1000;

export const CRAWLER_PERIODIC_CYCLE_EPOCH_MS = Date.UTC(2024, 0, 1, 0, 0, 0, 0);

export function floorToUtcHour(date: Date): Date {
  const floored = new Date(date.getTime());

  floored.setUTCMinutes(0, 0, 0);

  return floored;
}

export type CrawlerPeriodicWindowSettings = {
  enabled: boolean;
  windowEnabled: boolean;
  dayOfWeek?: number | null;
  startHour?: number | null;
  timezone?: string | null;
};

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function hashApplicationIdToSlot(applicationId: string): number {
  let hash = 2166136261;

  for (let index = 0; index < applicationId.length; index += 1) {
    hash ^= applicationId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) % CRAWLER_PERIODIC_SLOTS;
}

export function getCurrentCycleHourIndex({
  now = new Date(),
}: {
  now?: Date;
} = {}): number {
  const elapsedMs = now.getTime() - CRAWLER_PERIODIC_CYCLE_EPOCH_MS;
  const elapsedHours = Math.floor(elapsedMs / (60 * 60 * 1000));
  const normalized =
    ((elapsedHours % CRAWLER_PERIODIC_SLOTS) + CRAWLER_PERIODIC_SLOTS) %
    CRAWLER_PERIODIC_SLOTS;

  return normalized;
}

export function isCrawlerPeriodicDue({
  lastWeeklyCrawlAt,
  now = new Date(),
}: {
  lastWeeklyCrawlAt: Date | null | undefined;
  now?: Date;
}): boolean {
  if (!lastWeeklyCrawlAt) {
    return true;
  }

  return (
    now.getTime() - lastWeeklyCrawlAt.getTime() >=
    CRAWLER_PERIODIC_INTERVAL_MS - CRAWLER_PERIODIC_DUE_GRACE_MS
  );
}

function getLocalDayAndHour({
  now,
  timezone,
}: {
  now: Date;
  timezone: string;
}): { dayOfWeek: number; hour: number } | null {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const weekdayPart = parts.find((part) => part.type === 'weekday')?.value;
    const hourPart = parts.find((part) => part.type === 'hour')?.value;

    if (!weekdayPart || hourPart === undefined) {
      return null;
    }

    const dayOfWeek = WEEKDAY_TO_INDEX[weekdayPart];
    const hour = Number(hourPart === '24' ? '0' : hourPart);

    if (dayOfWeek === undefined || !Number.isFinite(hour)) {
      return null;
    }

    return { dayOfWeek, hour };
  } catch {
    return null;
  }
}

export function isHourInCrawlerWindow({
  hour,
  startHour,
}: {
  hour: number;
  startHour: number;
}): boolean {
  for (let offset = 0; offset < CRAWLER_PERIODIC_WINDOW_HOURS; offset += 1) {
    if ((startHour + offset) % 24 === hour) {
      return true;
    }
  }

  return false;
}

function isLocalTimeInCrawlerWindow({
  dayOfWeek,
  hour,
  startDayOfWeek,
  startHour,
}: {
  dayOfWeek: number;
  hour: number;
  startDayOfWeek: number;
  startHour: number;
}): boolean {
  for (let offset = 0; offset < CRAWLER_PERIODIC_WINDOW_HOURS; offset += 1) {
    const absoluteHour = startHour + offset;
    const windowHour = absoluteHour % 24;
    const windowDay = (startDayOfWeek + Math.floor(absoluteHour / 24)) % 7;

    if (dayOfWeek === windowDay && hour === windowHour) {
      return true;
    }
  }

  return false;
}

export function isWithinCrawlerWindow({
  settings,
  now = new Date(),
}: {
  settings: CrawlerPeriodicWindowSettings;
  now?: Date;
}): boolean {
  if (!settings.windowEnabled) {
    return false;
  }

  if (
    settings.dayOfWeek === null ||
    settings.dayOfWeek === undefined ||
    settings.startHour === null ||
    settings.startHour === undefined ||
    !settings.timezone?.trim()
  ) {
    return false;
  }

  const local = getLocalDayAndHour({
    now,
    timezone: settings.timezone.trim(),
  });

  if (!local) {
    return false;
  }

  return isLocalTimeInCrawlerWindow({
    dayOfWeek: local.dayOfWeek,
    hour: local.hour,
    startDayOfWeek: settings.dayOfWeek,
    startHour: settings.startHour,
  });
}

export function isAutoCrawlerSlotEligible({
  applicationId,
  now = new Date(),
}: {
  applicationId: string;
  now?: Date;
}): boolean {
  return (
    hashApplicationIdToSlot(applicationId) === getCurrentCycleHourIndex({ now })
  );
}

export function isCrawlerPeriodicEligibleNow({
  applicationId,
  settings,
  lastWeeklyCrawlAt,
  now = new Date(),
}: {
  applicationId: string;
  settings: CrawlerPeriodicWindowSettings | null | undefined;
  lastWeeklyCrawlAt: Date | null | undefined;
  now?: Date;
}): boolean {
  if (!settings?.enabled) {
    return false;
  }

  if (!isCrawlerPeriodicDue({ lastWeeklyCrawlAt, now })) {
    return false;
  }

  if (settings.windowEnabled) {
    return isWithinCrawlerWindow({ settings, now });
  }

  return isAutoCrawlerSlotEligible({ applicationId, now });
}

function getNextWindowOccurrenceUtc({
  after,
  dayOfWeek,
  startHour,
  timezone,
}: {
  after: Date;
  dayOfWeek: number;
  startHour: number;
  timezone: string;
}): Date {
  const cursor = new Date(after.getTime());

  for (let hourOffset = 0; hourOffset < 14 * 24 + 24; hourOffset += 1) {
    const candidate = new Date(cursor.getTime() + hourOffset * 60 * 60 * 1000);

    candidate.setUTCMinutes(0, 0, 0);

    const local = getLocalDayAndHour({ now: candidate, timezone });

    if (!local) {
      continue;
    }

    if (
      isLocalTimeInCrawlerWindow({
        dayOfWeek: local.dayOfWeek,
        hour: local.hour,
        startDayOfWeek: dayOfWeek,
        startHour,
      }) &&
      candidate.getTime() > after.getTime()
    ) {
      return candidate;
    }
  }

  const fallback = new Date(after.getTime() + 14 * 24 * 60 * 60 * 1000);

  fallback.setUTCMinutes(0, 0, 0);

  return fallback;
}

function getNextAutoSlotUtc({
  after,
  applicationId,
}: {
  after: Date;
  applicationId: string;
}): Date {
  const targetSlot = hashApplicationIdToSlot(applicationId);
  const cursor = new Date(after.getTime());

  cursor.setUTCMinutes(0, 0, 0);

  for (
    let hourOffset = 1;
    hourOffset <= CRAWLER_PERIODIC_SLOTS + 1;
    hourOffset += 1
  ) {
    const candidate = new Date(cursor.getTime() + hourOffset * 60 * 60 * 1000);

    if (getCurrentCycleHourIndex({ now: candidate }) === targetSlot) {
      return candidate;
    }
  }

  return new Date(after.getTime() + CRAWLER_PERIODIC_INTERVAL_MS);
}

export function getNextPeriodicCrawlerRunUtc({
  after,
  applicationId,
  settings,
  lastWeeklyCrawlAt,
}: {
  after: Date;
  applicationId?: string;
  settings?: CrawlerPeriodicWindowSettings | null;
  lastWeeklyCrawlAt?: Date | null;
}): Date {
  const dueAfter = lastWeeklyCrawlAt
    ? new Date(lastWeeklyCrawlAt.getTime() + CRAWLER_PERIODIC_INTERVAL_MS)
    : after;
  const searchAfter = dueAfter.getTime() > after.getTime() ? dueAfter : after;

  if (
    settings?.windowEnabled &&
    settings.dayOfWeek !== null &&
    settings.dayOfWeek !== undefined &&
    settings.startHour !== null &&
    settings.startHour !== undefined &&
    settings.timezone?.trim()
  ) {
    return getNextWindowOccurrenceUtc({
      after: searchAfter,
      dayOfWeek: settings.dayOfWeek,
      startHour: settings.startHour,
      timezone: settings.timezone.trim(),
    });
  }

  if (applicationId) {
    return getNextAutoSlotUtc({ after: searchAfter, applicationId });
  }

  const nextHour = new Date(searchAfter.getTime());

  nextHour.setUTCMinutes(0, 0, 0);
  nextHour.setUTCHours(nextHour.getUTCHours() + 1);

  return nextHour;
}
